/**
 * elevationEditOps — the elevations edit-operations core.
 *
 * PURE computations for the elevations **measurement** edits — the only
 * persisted edit surface of the elevations viewer. Given a measurement (or a
 * measurement key) and the current per-wall removed-list / offset map, these
 * functions compute the new value the handler should persist. They never touch
 * React, the DOM, or Firestore — the caller (ElevationsViewer's measurement
 * handlers) threads the returned value into setState + structureService, exactly
 * as before.
 *
 * Mirrors the floorplan precedent (`floorplanEditOps.js` + its `__tests__`):
 * the "what" (compute) layer is pure + unit-tested; the "how" (setState +
 * persistence) stays in the handler.
 *
 * Behavior-identical extraction from `ElevationsViewer.js`:
 *   - computeRemovedMeasurementKey   ← handleRemoveMeasurement      (:649-727)
 *   - addRemovedMeasurement          ← handleRemoveMeasurement      (:730-733)
 *   - measurementKeysEqual           ← handleRestoreMeasurement     (:771-793)
 *   - removeMeasurementKey           ← handleRestoreMeasurement     (:769-793)
 *   - mergeMeasurementOffset         ← handleMeasurementOffsetSave  (:830-836)
 *
 * Reuses existing primitives (does NOT reinvent):
 *   - MEASUREMENT_ORIENTATION / MEASUREMENT_POSITION (utils/measurementConstants)
 *
 * NOTE on scope: object/wall "selection" in the elevations viewer is transient
 * React UI state (setSelectedObject / setSelectedWallGroup) — it neither
 * computes geometry nor persists, so there is nothing pure to extract there.
 * `handleResetAllMeasurements` is thin persistence (clear local state + write
 * empty list / empty offsets to Firestore); its only "compute" is the empty
 * values, which the trivial helpers below name for symmetry with the handler.
 */

import { measurementKey as buildMeasurementKey } from '@shared-lib/dimensioning';
import {
  getPositionDescription,
  MEASUREMENT_ORIENTATION,
  MEASUREMENT_POSITION,
} from '../../utils/measurementConstants';

/**
 * A whole dimension LINE's key (`str:<h|v>:<side>:<tier>`), as the tier
 * builder stamps it on every segment of that line. Stored in the same
 * `removedMeasurements` array as a single dimension's key.
 */
export const STRING_KEY_PREFIX = 'str:';

/**
 * The key for the LINE a measurement sits on, or null when it is not part of
 * one (the `all` mode's chains are not strings). PURE.
 */
export function measurementStringKey(measurement) {
  return measurement?.stringKey || null;
}

/**
 * @typedef {Object} MeasurementKeyEnd
 * @property {string} objectId
 * @property {number} orientation  MEASUREMENT_ORIENTATION value.
 * @property {number} position     MEASUREMENT_POSITION value.
 *
 * @typedef {Object} MeasurementKey
 * @property {MeasurementKeyEnd} from
 * @property {MeasurementKeyEnd} to
 */

/**
 * Resolve a measurement endpoint to a stable object id. Mirrors the inline
 * `getObjectId` in handleRemoveMeasurement: prefer the nested `.object.id`
 * (measurement points carry their source under `.object`), then a direct `.id`,
 * then a type-based fallback (wall → wallIdentifier, floor/ceiling → literal,
 * else `${type}_${x||y}`). PURE.
 *
 * @param {Object} point          a measurement endpoint (fromPoint / toPoint or its .object).
 * @param {string} wallIdentifier the active wall id (used for the wall fallback).
 * @returns {string}
 */
export function resolveMeasurementObjectId(point, wallIdentifier) {
  // Check nested object first (from measurement points).
  if (point.object) {
    return point.object.id;
  }
  // Check direct properties (shouldn't happen but just in case).
  if (point.id) {
    return point.id;
  }
  // Fallback to type-based ID.
  if (point.type?.includes('wall')) return wallIdentifier;
  if (point.type?.includes('floor')) return 'floor';
  if (point.type?.includes('ceiling')) return 'ceiling';
  return `${point.type}_${point.x || point.y}`;
}

/**
 * Resolve a measurement endpoint to a MEASUREMENT_POSITION. Mirrors the inline
 * `getPosition` in handleRemoveMeasurement: for a horizontal measurement the
 * left/right/center bands map to START/END/CENTER; for a vertical one the
 * top/bottom/center bands map to START/END/CENTER. Detection uses both the
 * point `type` and an optional human `label`. Defaults to CENTER. PURE.
 *
 * @param {string}  pointType
 * @param {string}  pointLabel
 * @param {boolean} isHorizontal
 * @returns {number} MEASUREMENT_POSITION value.
 */
export function resolveMeasurementPosition(
  pointType,
  pointLabel,
  isHorizontal
) {
  if (isHorizontal) {
    if (
      pointType?.includes('start') ||
      pointType?.includes('wall-start') ||
      pointLabel?.includes('Left')
    )
      return MEASUREMENT_POSITION.START;
    if (
      pointType?.includes('end') ||
      pointType?.includes('wall-end') ||
      pointLabel?.includes('Right')
    )
      return MEASUREMENT_POSITION.END;
    if (pointType?.includes('center') || pointLabel?.includes('Center'))
      return MEASUREMENT_POSITION.CENTER;
  } else {
    if (
      pointType?.includes('top') ||
      pointType?.includes('ceiling') ||
      pointLabel?.includes('Top')
    )
      return MEASUREMENT_POSITION.START;
    if (
      pointType?.includes('bottom') ||
      pointType?.includes('floor') ||
      pointLabel?.includes('Bottom')
    )
      return MEASUREMENT_POSITION.END;
    if (pointType?.includes('center') || pointLabel?.includes('Center'))
      return MEASUREMENT_POSITION.CENTER;
  }
  return MEASUREMENT_POSITION.CENTER;
}

/**
 * MEASUREMENT REMOVE — the key a hidden measurement is RECORDED under: the one
 * ref-based `dim:` string that drag offsets already use
 * (`@shared-lib/dimensioning`). Returns `null` when either endpoint cannot be
 * resolved, and the handler skips the hide rather than writing an identity
 * nothing will match again. PURE.
 *
 * Hides used to be written as the `{from, to}` struct below, whose position
 * came from the OBJECT's type (`upper-cabinet`, never `object-start`) and then
 * its label — so both edges of any labelled object resolved to CENTER and two
 * different dimensions between the same pair were indistinguishable. Entries
 * already stored in that form are still READ, via
 * `legacyRemovedMeasurementKey`.
 *
 * @param {Object} measurement      the measurement being hidden.
 * @param {string} wallIdentifier   the active wall id.
 * @returns {string|null}
 */
export function computeRemovedMeasurementKey(measurement, wallIdentifier) {
  if (!measurement?.fromPoint || !measurement?.toPoint) return null;
  return buildMeasurementKey(measurement, { wallId: wallIdentifier });
}

/**
 * The `{from, to}` struct hides were written as before ref keys existed,
 * reproduced EXACTLY — bug included — so entries already in Firestore still
 * match. READ path only; nothing writes this form any more. PURE.
 *
 * @param {Object} measurement      the measurement being matched.
 * @param {string} wallIdentifier   the active wall id (for wall-id fallback).
 * @returns {MeasurementKey|null}
 */
export function legacyRemovedMeasurementKey(measurement, wallIdentifier) {
  if (!measurement?.fromPoint || !measurement?.toPoint) {
    return null;
  }

  // Measurement points nest their source under `.object`.
  const fromObj = measurement.fromPoint.object || measurement.fromPoint;
  const toObj = measurement.toPoint.object || measurement.toPoint;

  const fromId = resolveMeasurementObjectId(fromObj, wallIdentifier);
  const toId = resolveMeasurementObjectId(toObj, wallIdentifier);

  const isHorizontal = measurement.type === 'horizontal';
  const orientation = isHorizontal
    ? MEASUREMENT_ORIENTATION.HORIZONTAL
    : MEASUREMENT_ORIENTATION.VERTICAL;

  const fromPos = resolveMeasurementPosition(
    fromObj.type,
    fromObj.label || measurement.fromLabel,
    isHorizontal
  );
  const toPos = resolveMeasurementPosition(
    toObj.type,
    toObj.label || measurement.toLabel,
    isHorizontal
  );

  return {
    from: { objectId: fromId, orientation, position: fromPos },
    to: { objectId: toId, orientation, position: toPos },
  };
}

/**
 * MEASUREMENT REMOVE — append a removed-measurement key to a wall's removed
 * list, immutably. PURE.
 *
 * Mirrors the inline `[...(removedMeasurements[wallId] || []), key]` spread in
 * handleRemoveMeasurement (:730-733).
 *
 * @param {MeasurementKey[]} removedList  the wall's current removed list (may be undefined).
 * @param {MeasurementKey}   key          the key to append.
 * @returns {MeasurementKey[]} a new list with the key appended.
 */
export function addRemovedMeasurement(removedList, key) {
  return [...(removedList || []), key];
}

/**
 * Structural equality for two measurement keys (both ends + both fields). Both
 * must be well-formed {from, to} objects. Mirrors the comparison inside
 * handleRestoreMeasurement's filter (:782-789). PURE.
 *
 * @param {MeasurementKey} a
 * @param {MeasurementKey} b
 * @returns {boolean}
 */
export function measurementKeysEqual(a, b) {
  // Ref keys are strings and compare as such; a string never equals a struct.
  if (typeof a === 'string' || typeof b === 'string') return a === b;
  if (
    typeof a !== 'object' ||
    !a?.from ||
    !a?.to ||
    typeof b !== 'object' ||
    !b?.from ||
    !b?.to
  ) {
    return false;
  }
  return (
    a.from.objectId === b.from.objectId &&
    a.from.orientation === b.from.orientation &&
    a.from.position === b.from.position &&
    a.to.objectId === b.to.objectId &&
    a.to.orientation === b.to.orientation &&
    a.to.position === b.to.position
  );
}

/**
 * MEASUREMENT RESTORE — remove a measurement key from a wall's removed list,
 * immutably. Malformed (non-{from,to}) items are KEPT, matching the original
 * filter's "keep any malformed items" branch. PURE.
 *
 * Mirrors handleRestoreMeasurement (:769-793).
 *
 * @param {MeasurementKey[]} removedList    the wall's current removed list (may be undefined).
 * @param {MeasurementKey}   measurementKey the key to restore (remove from the list).
 * @returns {MeasurementKey[]} a new list without the matching key.
 */
export function removeMeasurementKey(removedList, measurementKey) {
  // One measurement can be stored under either identity, so restoring it takes
  // both: the `dim:` string it is written as now, and the legacy struct an
  // older hide left behind. Callers pass an array to clear both at once.
  const keys = (
    Array.isArray(measurementKey) ? measurementKey : [measurementKey]
  ).filter((k) => k !== null && k !== undefined);

  return (removedList || []).filter((item) => {
    const matches = keys.some((key) => {
      if (typeof item === 'string' || typeof key === 'string') {
        return measurementKeysEqual(item, key);
      }
      // Only well-formed object keys participate in the structural compare.
      if (item?.from && item?.to && key?.from && key?.to) {
        return measurementKeysEqual(item, key);
      }
      return false;
    });
    // Keep everything that did not match (malformed items included).
    return !matches;
  });
}

/**
 * Is this measurement hidden by the wall's removed list? Checks the `dim:`
 * string a hide is written as today AND the legacy struct, so a hide made
 * before the key change still suppresses its dimension. PURE.
 *
 * The one place this question is answered — `RenderWallElevation` carried a
 * second inline copy of the struct logic, which is how the two drifted.
 *
 * @param {Array}  removedList     the wall's removed list (may be undefined).
 * @param {Object} measurement     the measurement being drawn.
 * @param {string} wallIdentifier  the active wall id.
 * @returns {boolean}
 */
export function isMeasurementHidden(removedList, measurement, wallIdentifier) {
  if (!removedList?.length) return false;
  const key = computeRemovedMeasurementKey(measurement, wallIdentifier);
  const legacy = legacyRemovedMeasurementKey(measurement, wallIdentifier);
  return removedList.some((item) => {
    if (typeof item === 'string') {
      // A whole dimension LINE, hidden as one: every segment on it goes.
      if (item.startsWith(STRING_KEY_PREFIX)) {
        return measurement?.stringKey === item;
      }
      return key !== null && measurementKeysEqual(item, key);
    }
    return legacy !== null && measurementKeysEqual(item, legacy);
  });
}

/**
 * A human-readable description of one stored hide, for the hidden-measurements
 * panel, in either identity. PURE.
 *
 * @param {string|MeasurementKey} entry
 * @returns {{description: string, orientation: string, from: string, to: string}|null}
 */
export function describeRemovedMeasurement(entry) {
  if (typeof entry === 'string' && entry.startsWith(STRING_KEY_PREFIX)) {
    // `str:<h|v>:<side>:<tier>` — a whole dimension line.
    const [, axis, side, tier] = entry.split(':');
    if (!side || !tier) return null;
    const name = `${side} ${tier} dimension line`;
    return {
      description: name.charAt(0).toUpperCase() + name.slice(1),
      orientation: axis === 'h' ? 'Horizontal' : 'Vertical',
      from: side,
      to: tier,
    };
  }

  if (typeof entry === 'string') {
    // `dim:<h|v>:<endpoint>|<endpoint>` — see @shared-lib/dimensioning.
    const match = /^dim:([hv]):(.+)\|(.+)$/.exec(entry);
    if (!match) return null;
    const [, axis, from, to] = match;
    return {
      description: `${from} to ${to}`,
      orientation: axis === 'h' ? 'Horizontal' : 'Vertical',
      from,
      to,
    };
  }

  if (!entry?.from || !entry?.to) return null;
  const { from, to } = entry;
  const fromDesc = `${from.objectId} (${getPositionDescription(from.orientation, from.position)})`;
  const toDesc = `${to.objectId} (${getPositionDescription(to.orientation, to.position)})`;
  return {
    description: `${fromDesc} to ${toDesc}`,
    orientation:
      from.orientation === MEASUREMENT_ORIENTATION.HORIZONTAL
        ? 'Horizontal'
        : 'Vertical',
    from: fromDesc,
    to: toDesc,
  };
}

/**
 * MEASUREMENT OFFSET — merge a single measurement's drag offset into a wall's
 * offset map, immutably. PURE.
 *
 * Mirrors the nested spread in handleMeasurementOffsetSave (:830-836):
 *   { ...(prev[wallId] || {}), [measurementKey]: offset }.
 *
 * @param {Object} offsetsForWall   the wall's current offsets map (may be undefined).
 * @param {string} measurementKey   the measurement key (string, from generateMeasurementKey).
 * @param {*}      offset           the offset value to store.
 * @returns {Object} a new offsets map with the measurement's offset set.
 */
export function mergeMeasurementOffset(offsetsForWall, measurementKey, offset) {
  return {
    ...(offsetsForWall || {}),
    [measurementKey]: offset,
  };
}
